import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import HealingIcon from '@mui/icons-material/Healing';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import TimelineIcon from '@mui/icons-material/Timeline';
import BoltIcon from '@mui/icons-material/Bolt';
import WifiIcon from '@mui/icons-material/Wifi';
import SpeedIcon from '@mui/icons-material/Speed';
import RouterIcon from '@mui/icons-material/Router';
import HubIcon from '@mui/icons-material/Hub';
import ExploreIcon from '@mui/icons-material/Explore';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

import OptimizeMetaPanel from './OptimizeMetaPanel';
import OptimizeSchedulePanel from './OptimizeSchedulePanel';
import IperfSchedulePanel from './IperfSchedulePanel';
import NeighborScanSchedulePanel from './NeighborScanSchedulePanel';
import RadarIcon from '@mui/icons-material/Radar';
import HealthPanel from './HealthPanel';
import BackupParentsPanel from './BackupParentsPanel';
import ChannelsPanel from './ChannelsPanel';
import PowerPlanPanel from './PowerPlanPanel';
import SegmentPanel from './SegmentPanel';
import SnapshotsPanel from './SnapshotsPanel';
import PlansTabs from './PlansTabs';
import IperfTestPanel from './IperfTestPanel';
import NeighborsTabContainer from '../NeighborsTabContainer';
import TmCandidatesTab from '../TmCandidatesTab';
import ChannelSurveyTab from './ChannelSurveyTab';
import { NeighborDataProvider } from './NeighborDataContext';
import { OptimizeScopeProvider } from './OptimizeScopeContext';
import { consumeOptimizeScope } from '../../utils/optimizeScopeUtils';
import { consumeIperfCapScope } from '../../utils/iperfScopeUtils';
import { consumeSegmentCohortScope } from '../../utils/segmentScopeUtils';
import { persistOptimizeActiveSub, readOptimizeActiveSub } from '../../utils/segmentProposalUtils';
import { auth } from '../../services/auth';

const LIMITED_RRM_HIDDEN_TABS = new Set(['optimize', 'snapshots']);

const filterRrmTabs = (canWriteBackhaul) => TOP_TABS.filter((tab) => {
  if (LIMITED_RRM_HIDDEN_TABS.has(tab.id) && !canWriteBackhaul) return false;
  return true;
});

const defaultRrmTab = (canWriteBackhaul, pathname) => {
  if (pathname === '/neighbors') return 'neighbors';
  if (!canWriteBackhaul) return 'iperf-test';
  return 'optimize';
};

// Sub-tabs shown inside the Optimize top-level tab.
const OPTIMIZE_SUB_TABS = [
  {
    id: 'optimize-meta',
    label: 'Optimize',
    icon: <AutoFixHighIcon fontSize="small" />,
    tip: 'Run all or selected algorithms (Health → Topology → Power → Channels → Backup). Pick which algo steps to include in Parameters.',
    component: OptimizeMetaPanel,
    needsDevices: false,
  },
  {
    id: 'channels',
    label: 'Channels',
    icon: <RouterIcon fontSize="small" />,
    tip: 'Channel conflicts and map from the optimize planner (channel step) — same rules as the Optimize tab.',
    component: ChannelsPanel,
    needsDevices: true,
  },
  {
    id: 'power-plan',
    label: 'Power plan',
    icon: <BoltIcon fontSize="small" />,
    tip: 'Map-based TX power audit — visualize every link, review tier policy (−75 lower / −79 raise), preview and apply changes.',
    component: PowerPlanPanel,
    needsDevices: true,
  },
  {
    id: 'segment',
    label: 'Segment',
    icon: <HubIcon fontSize="small" />,
    tip: 'Partition the site into CAP-centric segments (best connectivity, depth-aware). Review on a map + list, then apply per segment or all.',
    component: SegmentPanel,
    needsDevices: true,
  },
  {
    id: 'health',
    label: 'Health',
    icon: <HealingIcon fontSize="small" />,
    tip: 'Single-snapshot triage — flags latency, jitter, weak uplink, long path, TX failures and offline devices.',
    component: HealthPanel,
    needsDevices: false,
  },
  {
    id: 'backup',
    label: 'Backup Parents',
    icon: <SettingsBackupRestoreIcon fontSize="small" />,
    tip: "Per-Node failover advisory using Yen's k-shortest paths.",
    component: BackupParentsPanel,
    needsDevices: false,
  },
];

// Sub-tabs of the top-level "Schedules" tab. The site runs one scheduled job
// at a time across both kinds, so they live side by side.
const SCHEDULE_SUB_TABS = [
  {
    id: 'optimize-schedule',
    label: 'Optimize Schedule',
    icon: <AutoFixHighIcon fontSize="small" />,
    tip: 'Scheduled optimize & apply (optionally with an iperf test first): what runs when (site time), the run in progress, why runs are on hold, and each last result.',
    component: OptimizeSchedulePanel,
  },
  {
    id: 'iperf-schedule',
    label: 'Iperf Schedule',
    icon: <SpeedIcon fontSize="small" />,
    tip: 'Scheduled iperf tests: start the same test as the Iperf tab (CAPs, test types, direction, timing) at a set site time. Optimize uses their hop measurements.',
    component: IperfSchedulePanel,
  },
  {
    id: 'neighbor-scan-schedule',
    label: 'Neighbours Scan Schedule',
    icon: <RadarIcon fontSize="small" />,
    tip: 'Scheduled neighbours scans: start the same scan as Neighbours → Run Scan (which devices, interval, channels, devices scanning at once) at a set site time. Each run appears in Recent Scans.',
    component: NeighborScanSchedulePanel,
  },
];

const SCHEDULES_SUB_STORAGE_KEY = 'rrm-schedules-active-sub';

const readSchedulesSub = () => {
  try {
    const stored = sessionStorage.getItem(SCHEDULES_SUB_STORAGE_KEY);
    if (SCHEDULE_SUB_TABS.some((t) => t.id === stored)) return stored;
  } catch {
    // ignore
  }
  return SCHEDULE_SUB_TABS[0].id;
};

// Top-level tabs. "Optimize" and "Schedules" act as groups that reveal
// their sub-tabs.
const TOP_TABS = [
  {
    id: 'optimize',
    label: 'Optimize',
    icon: <AutoFixHighIcon fontSize="small" />,
    tip: 'Run all or selected RRM algorithms (Health, Topology, TX Power, Channels, Backup Parents).',
  },
  {
    id: 'iperf-test',
    label: 'Iperf Test',
    icon: <SpeedIcon fontSize="small" />,
    tip: 'Run tree-wide iperf throughput tests (hop-by-hop and end-to-end) to identify interference bottlenecks.',
    component: IperfTestPanel,
    needsDevices: true,
  },
  {
    id: 'schedules',
    label: 'Schedules',
    icon: <EventRepeatIcon fontSize="small" />,
    tip: 'Scheduled optimize & apply and scheduled iperf tests for this site.',
  },
  {
    id: 'neighbors',
    label: 'Neighbors',
    icon: <WifiIcon fontSize="small" />,
    tip: 'Neighbour scan reports and on-demand scans for RF / tree diagnostics.',
    component: NeighborsTabContainer,
    needsDevices: true,
  },
  {
    id: 'channel-survey',
    label: 'Channel Survey',
    icon: <ExploreIcon fontSize="small" />,
    tip: 'ACS channel survey map — what each device heard on every scanned channel, at its GPS location.',
    component: ChannelSurveyTab,
    needsDevices: false,
  },
  {
    id: 'tm-candidates',
    label: 'TM Candidates',
    icon: <AccountTreeIcon fontSize="small" />,
    tip: 'Topology-management candidate parents for each jnet2 Node — view ranked candidates and the active selection.',
    component: TmCandidatesTab,
    needsDevices: true,
  },
  {
    id: 'snapshots',
    label: 'Snapshots',
    icon: <SaveAltIcon fontSize="small" />,
    tip: 'Save the post-analysis tree state and re-apply only the drift later.',
    component: SnapshotsPanel,
    needsDevices: false,
  },
  {
    id: 'plans',
    label: 'Plans',
    icon: <TimelineIcon fontSize="small" />,
    tip: 'Live monitor of analyser/snapshot apply runs and long-lived segment rollouts.',
    component: PlansTabs,
    needsDevices: false,
  },
];

const getTabScroller = (root) => root?.querySelector('.MuiTabs-scroller');

const ScrollableTabBar = ({
  tabs,
  value,
  onChange,
  ariaLabel,
  tabSx,
  selectedTabSx,
}) => {
  const tabsRootRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const scroller = getTabScroller(tabsRootRef.current);
    if (!scroller) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const { scrollLeft, clientWidth, scrollWidth } = scroller;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const scroller = getTabScroller(tabsRootRef.current);
    if (!scroller) return undefined;

    scroller.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(scroller);

    return () => {
      scroller.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, tabs]);

  useEffect(() => {
    const selected = tabsRootRef.current?.querySelector('[role="tab"][aria-selected="true"]');
    selected?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [value]);

  const scrollTabs = (direction) => {
    const scroller = getTabScroller(tabsRootRef.current);
    if (!scroller) return;
    scroller.scrollBy({ left: direction * Math.max(scroller.clientWidth * 0.6, 160), behavior: 'smooth' });
  };

  const showScrollButtons = canScrollLeft || canScrollRight;

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', maxWidth: '100%' }}>
      <Box ref={tabsRootRef} sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Tabs
          value={value}
          onChange={onChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label={ariaLabel}
          sx={tabSx}
        >
          {tabs.map((t) => (
            <Tab
              key={t.id}
              value={t.id}
              data-id={t.id}
              icon={t.icon}
              iconPosition="start"
              label={
                t.tip ? (
                  <Tooltip title={t.tip} placement="top">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      {t.label}
                    </Box>
                  </Tooltip>
                ) : t.label
              }
              sx={selectedTabSx?.(t)}
            />
          ))}
        </Tabs>
      </Box>
      {showScrollButtons && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            borderLeft: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <IconButton
            size="small"
            aria-label="Scroll tabs left"
            disabled={!canScrollLeft}
            onClick={() => scrollTabs(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Scroll tabs right"
            disabled={!canScrollRight}
            onClick={() => scrollTabs(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// ─── Nested Optimize panel ──────────────────────────────────────────────────
// Rendered inside the "optimize" top-level tab. Manages its own sub-tab bar
// and lazy-mount visited set so sub-panel state survives switching.
const OptimizeTabGroup = ({
  devices,
  isActive,
  onOpenPlan,
  initialSubTab,
  pendingOptimizeScope,
  onOptimizeScopeConsumed,
  pendingSegmentCohort,
  onSegmentCohortConsumed,
  registerScheduleSettings,
}) => {
  // A remembered sub-tab that no longer exists here (the schedule tabs moved
  // to the Schedules tab) falls back to the main Optimize view.
  const initialSub = () => {
    const stored = readOptimizeActiveSub('optimize-meta');
    return OPTIMIZE_SUB_TABS.some((t) => t.id === stored) ? stored : 'optimize-meta';
  };
  const [activeSub, setActiveSub] = useState(initialSub);
  const [visitedSub, setVisitedSub] = useState(() => new Set([initialSub()]));

  useEffect(() => {
    persistOptimizeActiveSub(activeSub);
  }, [activeSub]);

  useEffect(() => {
    // A string, or { id } when the same sub-tab must be re-selected.
    const id = typeof initialSubTab === 'string' ? initialSubTab : initialSubTab?.id;
    if (!id) return;
    setActiveSub(id);
    setVisitedSub((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, [initialSubTab]);

  const ensureVisitedSub = useCallback((id) => {
    setVisitedSub((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleSubChange = useCallback((_e, val) => {
    ensureVisitedSub(val);
    setActiveSub(val);
  }, [ensureVisitedSub]);

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          bgcolor: 'action.hover',
          borderRadius: '4px 4px 0 0',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <ScrollableTabBar
          tabs={OPTIMIZE_SUB_TABS}
          value={activeSub}
          onChange={handleSubChange}
          ariaLabel="Optimize sections"
          tabSx={{
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.85rem',
            },
            '& .MuiTab-root.Mui-selected': { fontWeight: 700 },
          }}
        />
      </Box>

      {OPTIMIZE_SUB_TABS.map((t) => {
        if (!visitedSub.has(t.id)) return null;
        const Cmp = t.component;
        const extraProps = {};
        if (t.id === 'optimize-meta') {
          extraProps.onOpenPlan = onOpenPlan;
          extraProps.devices = devices;
          extraProps.initialOptimizeScope = pendingOptimizeScope;
          extraProps.onOptimizeScopeApplied = onOptimizeScopeConsumed;
          extraProps.registerScheduleSettings = registerScheduleSettings;
        }
        if (t.id === 'segment') {
          extraProps.initialCohort = pendingSegmentCohort;
          extraProps.onCohortApplied = onSegmentCohortConsumed;
        }
        const subIsActive = isActive && t.id === activeSub;
        return (
          <Box key={t.id} sx={{ display: t.id === activeSub ? 'block' : 'none' }}>
            {t.needsDevices
              ? <Cmp devices={devices} isActive={subIsActive} {...extraProps} />
              : <Cmp isActive={subIsActive} {...extraProps} />}
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Schedules panel ────────────────────────────────────────────────────────
// Rendered inside the "schedules" top-level tab: Optimize Schedule and Iperf
// Schedule as sub-tabs, lazily mounted so each keeps its state.
const SchedulesTabGroup = ({
  devices,
  isActive,
  onOpenPlan,
  onOpenIperfRun,
  onGoToOptimize,
  getScheduleSettings,
}) => {
  const [activeSub, setActiveSub] = useState(readSchedulesSub);
  const [visitedSub, setVisitedSub] = useState(() => new Set([readSchedulesSub()]));

  const handleSubChange = useCallback((_e, val) => {
    setVisitedSub((prev) => {
      if (prev.has(val)) return prev;
      const next = new Set(prev);
      next.add(val);
      return next;
    });
    setActiveSub(val);
    try {
      sessionStorage.setItem(SCHEDULES_SUB_STORAGE_KEY, val);
    } catch {
      // ignore
    }
  }, []);

  return (
    <Box>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          bgcolor: 'action.hover',
          borderRadius: '4px 4px 0 0',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <ScrollableTabBar
          tabs={SCHEDULE_SUB_TABS}
          value={activeSub}
          onChange={handleSubChange}
          ariaLabel="Schedules sections"
          tabSx={{
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.85rem',
            },
            '& .MuiTab-root.Mui-selected': { fontWeight: 700 },
          }}
        />
      </Box>
      {SCHEDULE_SUB_TABS.map((t) => {
        if (!visitedSub.has(t.id)) return null;
        const Cmp = t.component;
        const extraProps = t.id === 'optimize-schedule'
          ? { onOpenPlan, onGoToOptimize, getScheduleSettings }
          : {};
        return (
          <Box key={t.id} sx={{ display: t.id === activeSub ? 'block' : 'none' }}>
            <Cmp
              isActive={isActive && t.id === activeSub}
              devices={devices}
              onOpenIperfRun={onOpenIperfRun}
              {...extraProps}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Top-level view ─────────────────────────────────────────────────────────
const TreeOptimizationView = ({ devices, isActive = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const canWriteBackhaul = auth.canWriteBackhaul();
  const visibleTopTabs = useMemo(
    () => filterRrmTabs(canWriteBackhaul),
    [canWriteBackhaul],
  );
  const [active, setActive] = useState(() =>
    defaultRrmTab(canWriteBackhaul, location.pathname),
  );
  // Track which top-level panels have ever been visited (lazy-mount).
  const [visited, setVisited] = useState(() => new Set([active]));

  // Deep-link a freshly-created plan run from any sibling panel into Plans.
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [pendingOptimizeScope, setPendingOptimizeScope] = useState(null);
  const [optimizeInitialSub, setOptimizeInitialSub] = useState(null);
  const [pendingIperfCapScope, setPendingIperfCapScope] = useState(null);
  const [pendingIperfRunId, setPendingIperfRunId] = useState(null);
  const [pendingSegmentCohort, setPendingSegmentCohort] = useState(null);
  // The Optimize sub-tab registers a getter for its current parameters, so
  // an optimize schedule (in the Schedules tab) can copy them.
  const scheduleSettingsRef = useRef(null);
  const registerScheduleSettings = useCallback((getter) => { scheduleSettingsRef.current = getter; }, []);
  const getScheduleSettings = useCallback(() => (scheduleSettingsRef.current ? scheduleSettingsRef.current() : null), []);

  const ensureVisited = useCallback((id) => {
    setVisited((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // Keep /neighbors as a bookmarkable alias for the Neighbors sub-tab.
  useEffect(() => {
    if (location.pathname === '/neighbors') {
      setActive('neighbors');
      ensureVisited('neighbors');
    } else if (location.pathname === '/analysis' && active === 'neighbors') {
      setActive(defaultRrmTab(canWriteBackhaul, '/analysis'));
    }
  }, [location.pathname, active, ensureVisited, canWriteBackhaul]);

  useEffect(() => {
    if (visibleTopTabs.some((tab) => tab.id === active)) return;
    const fallback = visibleTopTabs[0]?.id || 'iperf-test';
    setActive(fallback);
    ensureVisited(fallback);
  }, [active, visibleTopTabs, ensureVisited]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scope') !== 'devices') return;
    if (!canWriteBackhaul) {
      if (location.search) navigate('/analysis', { replace: true });
      return;
    }
    const stored = consumeOptimizeScope();
    if (stored?.type === 'devices' && stored.serials?.length) {
      setPendingOptimizeScope(stored);
      setOptimizeInitialSub('optimize-meta');
    }
    setActive('optimize');
    ensureVisited('optimize');
    if (location.search) navigate('/analysis', { replace: true });
  }, [location.pathname, location.search, ensureVisited, navigate, canWriteBackhaul]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scope') !== 'segment') return;
    if (!canWriteBackhaul) {
      // Drop any stashed cohort so a later legitimate navigation doesn't
      // consume a stale selection.
      consumeSegmentCohortScope();
      if (location.search) navigate('/analysis', { replace: true });
      return;
    }
    const stored = consumeSegmentCohortScope();
    if (stored?.serials?.length) {
      setPendingSegmentCohort(stored.serials);
      setOptimizeInitialSub('segment');
    }
    setActive('optimize');
    ensureVisited('optimize');
    if (location.search) navigate('/analysis', { replace: true });
  }, [location.pathname, location.search, ensureVisited, navigate, canWriteBackhaul]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('scope') !== 'iperf') return;
    const stored = consumeIperfCapScope();
    if (stored?.capSerials?.length) {
      setPendingIperfCapScope(stored);
    }
    setActive('iperf-test');
    ensureVisited('iperf-test');
    if (location.search) navigate('/analysis', { replace: true });
  }, [location.pathname, location.search, ensureVisited, navigate]);

  const handleChange = (_e, val) => {
    ensureVisited(val);
    if (val === 'neighbors') {
      if (location.pathname !== '/neighbors') navigate('/neighbors', { replace: true });
      setActive('neighbors');
    } else {
      if (location.pathname === '/neighbors') navigate('/analysis', { replace: true });
      setActive(val);
    }
  };

  const openPlan = useCallback((planId) => {
    if (!planId) return;
    setPendingPlanId(planId);
    ensureVisited('plans');
    setActive('plans');
  }, [ensureVisited]);

  const clearPendingPlan = useCallback(() => setPendingPlanId(null), []);
  const openIperfRun = useCallback((runId) => {
    if (!runId) return;
    setPendingIperfRunId(runId);
    ensureVisited('iperf-test');
    setActive('iperf-test');
  }, [ensureVisited]);
  const clearPendingIperfRun = useCallback(() => setPendingIperfRunId(null), []);
  const goToOptimize = useCallback(() => {
    setOptimizeInitialSub({ id: 'optimize-meta' });
    ensureVisited('optimize');
    setActive('optimize');
  }, [ensureVisited]);
  const clearPendingOptimizeScope = useCallback(() => {
    setPendingOptimizeScope(null);
    setOptimizeInitialSub(null);
  }, []);
  const clearPendingIperfCapScope = useCallback(() => setPendingIperfCapScope(null), []);
  const clearPendingSegmentCohort = useCallback(() => {
    setPendingSegmentCohort(null);
    setOptimizeInitialSub(null);
  }, []);

  return (
    <NeighborDataProvider isActive={isActive}>
      <OptimizeScopeProvider>
      <Box>
        {/* ── Top-level tab bar ── */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: '100%', maxWidth: '100%' }}>
          <ScrollableTabBar
            tabs={visibleTopTabs}
            value={active}
            onChange={handleChange}
            ariaLabel="RRM sections"
            tabSx={{
              '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 500 },
              '& .MuiTab-root.Mui-selected[data-id="optimize"]': { fontWeight: 700 },
            }}
            selectedTabSx={(t) => (t.id === 'optimize' ? { fontWeight: 700 } : undefined)}
          />
        </Box>

        {/* ── Optimize group (sub-tabs managed by OptimizeTabGroup) ── */}
        {canWriteBackhaul && visited.has('optimize') && (
          <Box sx={{ display: active === 'optimize' ? 'block' : 'none' }}>
            <OptimizeTabGroup
              devices={devices}
              isActive={active === 'optimize'}
              onOpenPlan={openPlan}
              initialSubTab={optimizeInitialSub}
              pendingOptimizeScope={pendingOptimizeScope}
              onOptimizeScopeConsumed={clearPendingOptimizeScope}
              pendingSegmentCohort={pendingSegmentCohort}
              onSegmentCohortConsumed={clearPendingSegmentCohort}
              registerScheduleSettings={registerScheduleSettings}
            />
          </Box>
        )}

        {/* ── Schedules group (sub-tabs managed by SchedulesTabGroup) ── */}
        {visited.has('schedules') && (
          <Box sx={{ display: active === 'schedules' ? 'block' : 'none' }}>
            <SchedulesTabGroup
              devices={devices}
              isActive={active === 'schedules'}
              onOpenPlan={openPlan}
              onOpenIperfRun={openIperfRun}
              onGoToOptimize={canWriteBackhaul ? goToOptimize : undefined}
              getScheduleSettings={getScheduleSettings}
            />
          </Box>
        )}

        {/* ── Other top-level panels ── */}
        {visibleTopTabs.filter((t) => t.id !== 'optimize' && t.id !== 'schedules').map((t) => {
          if (!visited.has(t.id)) return null;
          const Cmp = t.component;
          const extraProps = {};
          if (t.id === 'plans') {
            extraProps.initialPlanId = pendingPlanId;
            extraProps.onClearInitialPlan = clearPendingPlan;
            extraProps.onOpenPlan = openPlan;
          }
          if (t.id === 'snapshots') {
            extraProps.onOpenPlan = openPlan;
          }
          if (t.id === 'iperf-test') {
            extraProps.initialCapSerials = pendingIperfCapScope?.capSerials;
            extraProps.initialFocusSerials = pendingIperfCapScope?.focusSerials;
            extraProps.onCapScopeApplied = clearPendingIperfCapScope;
            extraProps.initialRunId = pendingIperfRunId;
            extraProps.onInitialRunApplied = clearPendingIperfRun;
          }
          const isActive = t.id === active;
          const neighborProps = t.id === 'neighbors' ? { allowNeighborsScan: canWriteBackhaul } : {};
          const planProps = t.id === 'plans' ? { allowPlanDelete: canWriteBackhaul } : {};
          return (
            <Box key={t.id} sx={{ display: isActive ? 'block' : 'none' }}>
              {t.needsDevices
                ? <Cmp devices={devices} isActive={isActive} {...extraProps} {...neighborProps} {...planProps} />
                : <Cmp isActive={isActive} {...extraProps} {...neighborProps} {...planProps} />}
            </Box>
          );
        })}
      </Box>
      </OptimizeScopeProvider>
    </NeighborDataProvider>
  );
};

export default TreeOptimizationView;
